import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import './TestCases.css';
 
// Limites de caracteres para campos de Casos de Teste
const FIELD_LIMITS = {
  description: 1000,
  steps: 5000,
  expected_result: 1000
};

const TestCases = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = useAuth();
  const [testCases, setTestCases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps: '',
    expected_result: '',
    priority: 'medium',
    status: 'draft',
    project_id: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testCaseToDelete, setTestCaseToDelete] = useState(null);
  // Alternância de visualização Lista/Quadro
  // Removido: manter apenas visual de quadro (Kanban)

    // Controle de permissão para criar caso de teste
    const canCreateTestCase = useMemo(() => {
        // Usa chave alinhada ao backend e fallback para roles globais com acesso
        // Removido papéis de projeto (LEADER, DESIGNER) e incluído QA
        return hasPermission('testcases.create', ['ADMIN', 'ADM', 'QA']);
    }, [hasPermission]);

    // Controle de permissão para excluir caso de teste
    const canDeleteTestCase = useMemo(() => {
        return hasPermission('testcases.delete', ['ADMIN', 'ADM', 'QA']);
    }, [hasPermission]);

    // Detecta alterações no formulário em modo de edição
    const hasChanges = useMemo(() => {
        if (!editingTestCase) return true; // Em modo criação, manter habilitado
        const original = editingTestCase;
        return (
            String(formData.title || '') !== String(original.title || '') ||
            String(formData.description || '') !== String(original.description || '') ||
            String(formData.steps || '') !== String(original.steps || '') ||
            String(formData.expected_result || '') !== String(original.expected_result || '') ||
            String(formData.priority || '') !== String(original.priority || '') ||
            String(formData.status || '') !== String(original.status || '') ||
            String(formData.project_id || '') !== String(original.project_id || '')
        );
    }, [editingTestCase, formData]);

    const boardColumns = useMemo(() => {
        const columns = {
            backlog: { title: 'Backlog', testCases: [] },
            planned: { title: 'Planejados', testCases: [] },
            testing: { title: 'Testando', testCases: [] },
            bug: { title: 'Bug/Falharam', testCases: [] },
            improvement: { title: 'Melhorias', testCases: [] },
            completed: { title: 'Executados com sucesso', testCases: [] },
        };

        testCases.forEach(tc => {
            const run = String(tc.last_run_status || '').toLowerCase();
            const result = String(tc.last_result_status || '').toLowerCase();

            // 1) Falhas sempre em Bug/Falharam; bloqueados ficam em Planejados
            if (result === 'failed') {
                columns.bug.testCases.push(tc);
                return;
            }
            if (result === 'blocked') {
                columns.planned.testCases.push(tc);
                return;
            }

            // 1.1) Melhorias em coluna própria
            if (result === 'improvement') {
                columns.improvement.testCases.push(tc);
                return;
            }

            // 2) Executados com sucesso têm prioridade sobre status de execução
            if (result === 'passed') {
                columns.completed.testCases.push(tc);
                return;
            }

            // 3) Execução iniciada (active/running) vai para "Testando"
            if (run === 'active' || run === 'running') {
                columns.testing.testCases.push(tc);
                return;
            }

            // 4) Reteste sem execução ativa deve voltar para planejamento
            if (result === 'retest') {
                columns.planned.testCases.push(tc);
                return;
            }

            // 5) Planejada/não iniciada (planned/pending) vai para "Planejados"
            if (run === 'planned' || run === 'pending') {
                columns.planned.testCases.push(tc);
                return;
            }

            // 6) Default (inclui 'skipped' e demais não mapeados) permanece em Backlog
            columns.backlog.testCases.push(tc);
        });

        return columns;
    }, [testCases]);

    const validateForm = () => {
        const errors = {};
        if (!String(formData.title || '').trim()) {
            errors.title = 'Título é obrigatório';
        }
        if (!String(formData.project_id || '').trim()) {
            errors.project_id = 'Projeto é obrigatório';
        }
        if (!String(formData.description || '').trim()) {
            errors.description = 'Descrição é obrigatória';
        }
        return errors;
    };

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => {
      if (!prev[field]) return prev;
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };



  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(getApiUrl('projects'), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.data && response.data.success) {
          const list = Array.isArray(response.data.projects)
            ? response.data.projects
            : (Array.isArray(response.data.data)
              ? response.data.data
              : (Array.isArray(response.data)
                ? response.data
                : []));
          setProjects(list);
        }
      } catch (error) {
        console.error('Erro ao buscar projetos:', error);
        // Usar dados mock se não conseguir conectar
        setProjects([
          { id: 1, name: 'Sistema de E-commerce' },
          { id: 2, name: 'App Mobile Banking' }
        ]);
      }
    };

    const fetchTestCases = async (projectId = null) => {
    try {
      setLoading(true);
      const url = projectId 
        ? getApiUrl(`testcases?project_id=${projectId}&include_last_result=true`)
        : getApiUrl('testcases?include_last_result=true');
      
      const token = localStorage.getItem('token');
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.success) {
        const list = Array.isArray(response.data.testCases)
          ? response.data.testCases
          : (Array.isArray(response.data.data)
            ? response.data.data
            : (Array.isArray(response.data)
              ? response.data
              : []));
        setTestCases(list);
      } else {
        console.error('Erro ao buscar casos de teste:', response.data.error);
      }
    } catch (error) {
      console.error('Erro ao buscar casos de teste:', error);
      // Se não conseguir conectar com a API, usar dados mock
      setTestCases([
        {
          id: 1,
          title: 'Login com credenciais válidas',
          description: 'Verificar se o usuário consegue fazer login com email e senha corretos',
          steps: '1. Acessar página de login\n2. Inserir email válido\n3. Inserir senha correta\n4. Clicar em "Entrar"',
          expected_result: 'Usuário deve ser redirecionado para o dashboard',
          priority: 'high',
          status: 'approved',
          project_id: 1,
          project_name: 'Sistema de E-commerce',
          created_at: '2024-01-15T10:00:00Z'
        },
        {
          id: 2,
          title: 'Adicionar produto ao carrinho',
          description: 'Verificar se é possível adicionar produtos ao carrinho de compras',
          steps: '1. Navegar para página do produto\n2. Selecionar quantidade\n3. Clicar em "Adicionar ao carrinho"',
          expected_result: 'Produto deve aparecer no carrinho com quantidade correta',
          priority: 'medium',
          status: 'approved',
          project_id: 1,
          project_name: 'Sistema de E-commerce',
          created_at: '2024-01-14T14:30:00Z'
        }
      ]);
    } finally {
      setLoading(false);
    }
    };

    fetchProjects();
    fetchTestCases();
  }, []);

  useEffect(() => {
    const fetchTestCasesForProject = async (projectId = null) => {
      try {
        setLoading(true);
        const url = projectId 
          ? getApiUrl(`testcases?project_id=${projectId}&include_last_result=true`)
          : getApiUrl('testcases?include_last_result=true');
        
        const token = localStorage.getItem('token');
        const response = await axios.get(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.data && response.data.success) {
          const list = Array.isArray(response.data.testCases)
            ? response.data.testCases
            : (Array.isArray(response.data.data)
              ? response.data.data
              : (Array.isArray(response.data)
                ? response.data
                : []));
          setTestCases(list);
        } else {
          console.error('Erro ao buscar casos de teste:', response.data.error);
        }
      } catch (error) {
        console.error('Erro ao buscar casos de teste:', error);
        setTestCases([]);
      } finally {
        setLoading(false);
      }
    };

    if (selectedProject) {
      fetchTestCasesForProject(selectedProject);
    } else {
      fetchTestCasesForProject();
    }
  }, [selectedProject]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showError('Preencha os campos obrigatórios.');
      return;
    }
    
    try {
      if (editingTestCase) {
        // Atualizar caso de teste existente
        const response = await axios.put(getApiUrl(`testcases/${editingTestCase.id}`), formData);
        
        if (response.data.success) {
          setTestCases(testCases.map(tc => 
            tc.id === editingTestCase.id ? response.data.testCase : tc
          ));
          setEditingTestCase(null);
          setShowCreateForm(false);
          showSuccess('Caso de teste atualizado com sucesso!');
        } else {
          showError(response.data.error || 'Erro ao atualizar caso de teste');
        }
      } else {
        // Verificar permissão antes de criar
        if (!canCreateTestCase) {
          showError('Você não tem permissão para criar casos de teste.');
          return;
        }
        // Criar novo caso de teste
        const response = await axios.post(getApiUrl('testcases'), formData);
        
        if (response.data.success) {
          // Refrescar lista para refletir filtros atuais e enriquecer dados
          try {
            const token = localStorage.getItem('token');
            const url = selectedProject 
              ? getApiUrl(`testcases?project_id=${selectedProject}&include_last_result=true`) 
              : getApiUrl('testcases?include_last_result=true');
            const refreshed = await axios.get(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (refreshed.data && refreshed.data.success) {
              const list = Array.isArray(refreshed.data.testCases)
                ? refreshed.data.testCases
                : (Array.isArray(refreshed.data.data)
                  ? refreshed.data.data
                  : (Array.isArray(refreshed.data)
                    ? refreshed.data
                    : []));
              setTestCases(list);
            } else {
              // fallback imediato mantém novo caso no topo
              setTestCases([response.data.testCase, ...testCases]);
            }
          } catch (_) {
            // fallback se falhar atualização
            setTestCases([response.data.testCase, ...testCases]);
          }
          setShowCreateForm(false);
          showSuccess('Caso de teste criado com sucesso!');
        } else {
          showError(response.data.error || 'Erro ao criar caso de teste');
        }
      }
      
      // Limpar formulário
      setFormData({
        title: '',
        description: '',
        steps: '',
        expected_result: '',
        priority: 'medium',
        status: 'draft',
        project_id: ''
      });
      setFormErrors({});
      
    } catch (error) {
      console.error('Erro ao salvar caso de teste:', error);
      const status = error?.response?.status;
      const backendMsg = error?.response?.data?.message || error?.response?.data?.error;
      if (status === 409) {
        // Duplicidade de caso de teste no mesmo projeto
        showError(backendMsg || 'Já existe um caso de teste com este título neste projeto.');
      } else {
        showError(backendMsg || 'Erro ao salvar caso de teste. Verifique sua conexão.');
      }
    }
  };

  const handleEdit = (testCase) => {
    setEditingTestCase(testCase);
    setFormData({
      title: testCase.title,
      description: testCase.description,
      steps: testCase.steps || '',
      expected_result: testCase.expected_result || '',
      priority: testCase.priority,
      status: testCase.status,
      project_id: testCase.project_id
    });
    setFormErrors({});
    setShowCreateForm(true);
  };

  const openDeleteModal = (testCase) => {
    if (!canDeleteTestCase) {
      showError('Você não tem permissão para excluir casos de teste.');
      return;
    }
    setTestCaseToDelete(testCase);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setTestCaseToDelete(null);
  };

  const confirmDelete = async () => {
    if (!testCaseToDelete) return;

    try {
      const token = localStorage.getItem('token') || '';
      const response = await axios.delete(getApiUrl(`testcases/${testCaseToDelete.id}`), {
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      });

      if (response.data.success) {
        setTestCases(prev => prev.filter(tc => String(tc.id) !== String(testCaseToDelete.id)));
        showSuccess('Caso de teste deletado com sucesso!');

        if (editingTestCase && String(editingTestCase.id) === String(testCaseToDelete.id)) {
          setEditingTestCase(null);
          setShowCreateForm(false);
        }
        closeDeleteModal();
      } else {
        showError(response.data.error || 'Erro ao deletar caso de teste');
      }
    } catch (error) {
      console.error('Erro ao deletar caso de teste:', error);
      const status = error?.response?.status;
      const backendMsg = error?.response?.data?.message || error?.response?.data?.error;
      if (status === 401) {
        showError(backendMsg || 'Sessão expirada ou não autenticado. Faça login novamente.');
      } else if (status === 403) {
        showError(backendMsg || 'Acesso negado. Permissão para excluir requerida.');
      } else if (status === 404) {
        setTestCases(prev => prev.filter(tc => String(tc.id) !== String(testCaseToDelete.id)));
        showSuccess('Caso de teste já foi deletado.');
      } else {
        showError(backendMsg || 'Erro ao deletar caso de teste. Tente novamente.');
      }
    } finally {
      closeDeleteModal();
    }
  };

  const handleDelete = (testCase) => {
    openDeleteModal(testCase);
  };

  // Navegar para a página de Execuções com filtro de projeto pré-selecionado
  const goToExecutions = (testCase) => {
    if (!testCase || !testCase.project_id) {
      navigate('/executions');
      return;
    }

    // Persistir filtros atuais incluindo o project_id do caso
    try {
      const saved = localStorage.getItem('executionFilters');
      const currentFilters = saved ? JSON.parse(saved) : {};
      const nextFilters = {
        ...currentFilters,
        project_id: String(testCase.project_id || ''),
        test_case_id: String(testCase.id || ''),
        search: ''
      };
      localStorage.setItem('executionFilters', JSON.stringify(nextFilters));
    } catch (_) {}

    navigate(`/executions?project_id=${encodeURIComponent(String(testCase.project_id))}&test_case_id=${encodeURIComponent(String(testCase.id))}`);
  };

  const cancelEdit = () => {
    setEditingTestCase(null);
    setShowCreateForm(false);
    setFormData({
      title: '',
      description: '',
      steps: '',
      expected_result: '',
      priority: 'medium',
      status: 'draft',
      project_id: ''
    });
    setFormErrors({});
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'highest':
      case 'critical': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      case 'lowest': return '#20c997';
      default: return '#6c757d';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'highest':
      case 'critical': return 'Crítica';
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      case 'lowest': return 'Muito Baixa';
      default: return priority;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#28a745';
      case 'review': return '#ffc107';
      case 'draft': return '#6c757d';
      case 'deprecated': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
     switch (status) {
       case 'draft': return 'Rascunho';
       case 'review': return 'Em Revisão';
       case 'approved': return 'Aprovado';
       case 'deprecated': return 'Depreciado';
       default: return status;
     }
   };

  // Duplicar um caso de teste
  const reloadCases = async () => {
    try {
      setLoading(true);
      const url = selectedProject 
        ? getApiUrl(`testcases?project_id=${selectedProject}&include_last_result=true`)
        : getApiUrl('testcases?include_last_result=true');
      const token = localStorage.getItem('token');
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data && response.data.success) {
        const list = Array.isArray(response.data.testCases)
          ? response.data.testCases
          : (Array.isArray(response.data.data)
            ? response.data.data
            : (Array.isArray(response.data)
              ? response.data
              : []));
        setTestCases(list);
      }
    } catch (err) {
      console.error('Erro ao recarregar casos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (tc) => {
    try {
      if (!canCreateTestCase) {
        showError('Você não tem permissão para duplicar casos.');
        return;
      }
      const token = localStorage.getItem('token');
      const response = await axios.post(getApiUrl(`testcases/${tc.id}/clone`), {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 201 && response.data?.success) {
        showSuccess('Caso de teste duplicado com sucesso');
        await reloadCases();
      } else {
        const backendMsg = response?.data?.message || response?.data?.error;
        showError(backendMsg || 'Erro ao duplicar caso de teste');
      }
    } catch (error) {
      const status = error?.response?.status;
      const backendMsg = error?.response?.data?.message || error?.response?.data?.error;
      if (status === 403) {
        showError(backendMsg || 'Acesso negado para duplicar casos neste projeto.');
      } else if (status === 404) {
        showError(backendMsg || 'Caso de teste não encontrado para duplicação.');
        await reloadCases();
      } else {
        showError(backendMsg || 'Erro ao duplicar caso de teste.');
      }
    }
  };

   // Badge com último resultado da execução
   const renderLastResultBadge = (tc) => {
     const status = tc.last_result_status;
     if (!status) return null;
     const map = {
       passed: { bg: '#28a745', text: 'Passou' },
       failed: { bg: '#dc3545', text: 'Falhou' },
       blocked: { bg: '#ffc107', text: 'Bloqueado', color: '#000' },
       skipped: { bg: '#17a2b8', text: 'Ignorado' },
       retest: { bg: '#6f42c1', text: 'Retestar' },
       improvement: { bg: '#0ea5e9', text: 'Melhoria' }
     };
     const info = map[String(status).toLowerCase()] || { bg: '#6c757d', text: status };
     const whenStr = tc.last_result_at ? new Date(tc.last_result_at).toLocaleString('pt-BR') : '';
     const runName = tc.last_run_name ? ` • Execução: ${tc.last_run_name}` : '';
     return (
       <span
         className="last-result-badge"
         title={`Último resultado${runName}${whenStr ? ` • ${whenStr}` : ''}`}
         style={{ backgroundColor: info.bg, color: info.color || '#fff', borderRadius: '6px', padding: '2px 8px', fontSize: '0.8rem', marginLeft: '8px' }}
       >
         Último: {info.text}
       </span>
     );
   };


  if (loading) {
    return (
      <div className="test-cases">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Carregando casos de teste...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="test-cases">
      <div className="test-cases-header">
        <div className="header-left">
          <button 
            className="btn-back"
            onClick={() => navigate('/overview')}
            title="Voltar ao Menu Principal"
          >
            ← Voltar ao Menu
          </button>
          <h1>🧪 Casos de Teste</h1>
        </div>
        <div className="header-actions">
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="project-filter"
          >
            <option value="">Todos os projetos</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {canCreateTestCase && (
            <button 
              className="btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              ➕ Novo Caso de Teste
            </button>
          )}
        </div>
      </div>

      {showCreateForm && (
        <div className="modal-overlay" onClick={cancelEdit}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTestCase ? 'Editar Caso de Teste' : 'Novo Caso de Teste'}</h2>
              <button className="modal-close" onClick={cancelEdit}>✕</button>
            </div>
            <form className="test-case-form" onSubmit={handleSubmit}>
              <div className="modal-body">
                <label>Título</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => updateFormField('title', e.target.value)}
                  placeholder="Digite o título do caso de teste"
                  maxLength={200}
                  required
                />
                {formErrors.title && <span className="error-text">{formErrors.title}</span>}
              </div>

              <div className="form-row">
                <label>Projeto</label>
                <select
                  value={formData.project_id || selectedProject || ''}
                  onChange={(e) => updateFormField('project_id', e.target.value)}
                  required
                >
                  <option value="">Selecione o projeto</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {formErrors.project_id && <span className="error-text">{formErrors.project_id}</span>}
              </div>

              <div className="form-row">
                <label>Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateFormField('description', e.target.value)}
                  placeholder="Descreva o caso de teste"
                  maxLength={FIELD_LIMITS.description}
                  rows={4}
                  required
                />
                {formErrors.description && <span className="error-text">{formErrors.description}</span>}
              </div>

              <div className="form-row">
                <label>Gherkin</label>
                <textarea
                  value={formData.steps}
                  onChange={(e) => updateFormField('steps', e.target.value)}
                  placeholder="Liste os passos necessários"
                  maxLength={FIELD_LIMITS.steps}
                  rows={5}
                />
              </div>

              <div className="form-row">
                <label>Resultado Esperado</label>
                <textarea
                  value={formData.expected_result}
                  onChange={(e) => updateFormField('expected_result', e.target.value)}
                  placeholder="Informe o resultado esperado"
                  maxLength={FIELD_LIMITS.expected_result}
                  rows={3}
                />
              </div>

              <div className="form-row inline">
                <div className="col">
                  <label>Prioridade</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => updateFormField('priority', e.target.value)}
                  >
                    <option value="highest">Muito Alta</option>
                    <option value="high">Alta</option>
                    <option value="medium">Média</option>
                    <option value="low">Baixa</option>
                    <option value="lowest">Muito Baixa</option>
                  </select>
                </div>
                <div className="col">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => updateFormField('status', e.target.value)}
                  >
                    <option value="draft">Rascunho</option>
                    <option value="review">Em Revisão</option>
                    <option value="approved">Aprovado</option>
                    <option value="deprecated">Depreciado</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                {editingTestCase && canDeleteTestCase && (
                  <button 
                    type="button"
                    className="btn-danger"
                    onClick={() => handleDelete(editingTestCase)}
                    style={{ marginRight: 'auto' }}
                  >
                    Excluir
                  </button>
                )}
                <button type="button" className="btn-secondary" onClick={cancelEdit}>Cancelar</button>
                <button type="submit" className="btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Visual de quadro (Kanban) único */}
      <div className="kanban-board">
        <div className="kanban-row">
          {["backlog","planned","testing","bug","improvement","completed"].map((key) => (
            <div key={key} className="kanban-column">
              <div className="kanban-column-header">
                <strong>{boardColumns[key].title}</strong>
                <span className="kanban-count">{boardColumns[key].testCases.length}</span>
              </div>
              <div className="kanban-cards">
                {boardColumns[key].testCases.length === 0 && (
                  <div className="empty-state"><p>Nada aqui</p></div>
                )}
                {boardColumns[key].testCases.map(tc => (
                  <div key={tc.id} className="test-case-card">
                    <div className="test-case-header">
                        <div className="title-row">
                          <span
                            className="status-dot"
                            style={{ backgroundColor: getStatusColor(tc.status) }}
                            title={`Status: ${getStatusText(tc.status)}`}
                            aria-label={`Status do caso: ${getStatusText(tc.status)}`}
                          ></span>
                          <h3 className="text-ellipsis">
                            {tc.title}
                          </h3>
                          <span
                            className="case-code-badge"
                            role="button"
                            tabIndex={0}
                            title="Editar caso de teste"
                            onClick={() => handleEdit(tc)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(tc); } }}
                          >
                            #{tc.code ?? tc.id ?? '-'}
                          </span>
                        </div>
                        <div className="meta-row">
                          <span className="project-name">
                            Projeto: {tc.project_name || (projects.find(p => String(p.id) === String(tc.project_id))?.name) || `Projeto #${tc.project_id}`}
                          </span>
                          <span className="responsible-name">Responsável: {tc.responsible_user_name || tc.responsible || 'Não definido'}</span>
                          <div className="last-result-row">
                            {renderLastResultBadge(tc)}
                            {tc.last_result_at && (
                              <span className="last-result-date">
                                {new Date(tc.last_result_at).toLocaleString('pt-BR')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="test-case-actions">
                          <button 
                            className="btn-secondary btn-sm"
                            onClick={() => goToExecutions(tc)}
                            title="Ver execuções deste caso"
                          >
                            📋
                          </button>
                          {canCreateTestCase && (
                            <button 
                              className="btn-secondary btn-sm"
                              onClick={() => handleDuplicate(tc)}
                              title="Duplicar caso de teste"
                            >
                              📑
                            </button>
                          )}
                          <button 
                            className="btn-secondary btn-sm"
                            onClick={() => handleEdit(tc)}
                            title="Editar caso de teste"
                          >
                            ✏️
                          </button>
                          {canDeleteTestCase && (
                            <button
                              className="btn-danger btn-sm"
                              onClick={() => handleDelete(tc)}
                              title="Excluir caso de teste"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    <div className="test-case-footer">
                        <div className="test-case-badges">
                          <span className="priority-badge" style={{ backgroundColor: getPriorityColor(tc.priority) }}>
                            Prioridade: {getPriorityText(tc.priority)}
                          </span>
                          <span className="status-badge" style={{ backgroundColor: getStatusColor(tc.status) }}>
                            Status: {getStatusText(tc.status)}
                          </span>
                        </div>
                      </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && testCaseToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar Exclusão</h2>
              <button className="close-btn" onClick={closeDeleteModal}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#991b1b', margin: 0, fontWeight: '500' }}>
                  ⚠️ Ação irreversível
                </p>
              </div>
              <p>
                Tem certeza que deseja deletar o caso de teste <strong>"{testCaseToDelete.title}"</strong>?
              </p>
              <p style={{ color: '#666', fontSize: '0.9em' }}>
                Esta ação não pode ser desfeita.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={closeDeleteModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                🗑️ Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestCases;
